import { useEffect, useState } from "react";
import { Receipt, Loader2, Download, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";
import { toast } from "sonner";

interface Transaction {
  id: string;
  status: string;
  billed_at: string | null;
  total: string | null;
  currency: string;
  invoice_number: string | null;
}

function formatAmount(total: string | null, currency: string): string {
  if (!total) return "—";
  // Paddle returns totals as minor units string (e.g. "2900" = $29.00)
  const amount = Number(total) / 100;
  if (!Number.isFinite(amount)) return total;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function formatDate(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" });
}

export function BillingHistoryCard() {
  const { mode } = useAuth();
  const { subscription } = useSubscription();
  const [txns, setTxns] = useState<Transaction[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "real" || !subscription) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke("list-transactions", {
        body: { environment: getPaddleEnvironment() },
      });
      if (cancelled) return;
      if (error) {
        setTxns([]);
      } else {
        setTxns(data?.transactions ?? []);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [mode, subscription?.id]);

  if (mode !== "real" || !subscription) return null;

  const downloadInvoice = async (id: string) => {
    setDownloadingId(id);
    try {
      const { data, error } = await supabase.functions.invoke("get-invoice-pdf", {
        body: { transactionId: id, environment: getPaddleEnvironment() },
      });
      if (error || !data?.url) {
        toast.error("Could not get invoice", { description: error?.message ?? "Try again later." });
        return;
      }
      window.open(data.url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Receipt className="h-4 w-4 text-primary" /> Billing history
        </CardTitle>
        <CardDescription>Past transactions and downloadable invoices.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading transactions…
          </div>
        ) : !txns || txns.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            No transactions yet. Your first invoice appears once your trial converts.
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {txns.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-3 gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{formatAmount(t.total, t.currency)}</span>
                    <Badge
                      variant={t.status === "completed" || t.status === "paid" ? "default" : "secondary"}
                      className="text-[10px]"
                    >
                      {t.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatDate(t.billed_at)}
                    {t.invoice_number ? ` · ${t.invoice_number}` : ""}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => downloadInvoice(t.id)}
                  disabled={downloadingId === t.id || (t.status !== "completed" && t.status !== "paid" && t.status !== "billed")}
                >
                  {downloadingId === t.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <><Download className="h-3.5 w-3.5 mr-1.5" /> Invoice</>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}